import { cloneDeep } from 'lodash-es';

import { perf } from './performance';
import { CallbackFunction, DcuplInitOptions } from './types';
import { AnalyticsController, AnalyticsEntrySdkActionValue } from './analytics.controller';
import { generateKey } from './helper';

export type DcuplUpdateScope = 'all' | 'global' | 'list' | 'list$';
export type DcuplUpdateTrigger = 'user' | 'loader';
export type DcuplUpdateType =
  | 'data_change'
  | 'dcupl_destroyed'
  | 'dcupl_handle_datacontainer'
  | 'dcupl_initialized'
  | 'dcupl_updated_manually'
  | 'filter_many'
  | 'filter_one'
  | 'fn_aggregate'
  | 'fn_facets'
  | 'fn_groupBy'
  | 'fn_metadata'
  | 'fn_pivot'
  | 'fn_suggest'
  | 'list_created'
  | 'list_destroyed'
  | 'list_updated'
  | 'loader_added'
  | 'loader_config_fetched'
  | 'loader_data_fetched'
  | 'loader_models_fetched'
  | 'loader_scripts_fetched'
  | 'loader_processed'
  | 'loader_removed'
  | 'model_add'
  | 'query_apply_options'
  | 'query_apply'
  | 'query_execute'
  | 'query_get'
  | 'query_has'
  | 'query_many'
  | 'query_one'
  | 'query_remove'
  | 'query_reset'
  | 'result_updated'
  | 'view_add';

export type DcuplUpdateAction = 'update';

export type DcuplUpdateMessage = {
  key?: number;
  scope: DcuplUpdateScope;
  type: DcuplUpdateType;
  method?: string;
  args?: any;
  action?: DcuplUpdateAction;
  // name?: string;
  origin?: DcuplUpdateTrigger;
  start?: number;
  end?: number;
  duration?: number;
  originCdRefKey?: string;
  modelKey?: string;
  listKey?: string;
  result?: any;
  cloneResult?: boolean;
  connectMessage?: boolean;
  connectMessageResult?: boolean;
};

export class ChangeDetector {
  public key!: string;
  protected parentCd: ChangeDetector | undefined;
  public dcuplInitOptions!: DcuplInitOptions;
  public analyticsController!: AnalyticsController;
  private off!: () => void;

  private updateListener: Set<CallbackFunction> = new Set();

  // only the "root" counter will be increased and used in the events
  private counter = 0;

  constructor() {}

  public _init(options: {
    cdRefKey: string;
    dcuplInitOptions: DcuplInitOptions;
    parentCd?: ChangeDetector;
    analyticsController: AnalyticsController;
  }): void {
    this.key = options.cdRefKey;
    this.analyticsController = options.analyticsController;

    this.parentCd = options.parentCd;

    this.dcuplInitOptions = options.dcuplInitOptions;

    if (this.parentCd) {
      this.off = this.parentCd.on((msg: DcuplUpdateMessage) => {
        if (msg.action === 'update' && msg.originCdRefKey === this.parentCd?.key) {
          // prevent connect message relay if the message is coming from the parent
          msg.connectMessage = false;
          this.trigger(msg);
        }
      });
    }
  }

  public getCounter(): number {
    if (this.parentCd) {
      return this.parentCd.getCounter();
    }
    return this.counter;
  }

  public increaseCounter(): void {
    if (this.parentCd) {
      this.parentCd.increaseCounter();
    } else {
      this.counter++;
    }
  }

  public destroy(): void {
    if (this.off) {
      this.off();
    }
  }

  public trigger(msg: DcuplUpdateMessage): void {
    if (!msg.originCdRefKey) {
      msg.originCdRefKey = this.key;
    }

    if (!msg.key) {
      this.increaseCounter();
      msg.key = this.getCounter();
    }

    if (!msg.end) {
      msg.end = perf.now();
      msg.duration = msg.start! - msg.end;
    }

    this.updateListener.forEach((cb) => {
      cb(msg);
    });
  }

  public on(cb: CallbackFunction): () => void {
    this.updateListener.add(cb);
    return () => {
      this.updateListener.delete(cb);
    };
  }
}

export const clone = (obj: any, shouldClone: boolean): any => {
  if (!shouldClone) {
    return obj;
  }
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj);
  } else {
    return cloneDeep(obj);
  }
};

const finish = (
  targetClass: any,
  cdRef: ChangeDetector,
  msg: DcuplUpdateMessage,
  start: number,
  context: any,
  result: any,
): any => {
  try {
    const shouldClone = !!cdRef.dcuplInitOptions.performance?.clone?.enabled && !!msg.cloneResult;

    const valueToReturn = clone(result, shouldClone);

    if (msg.scope === 'list') {
      msg.listKey = targetClass.cdRef?.key || targetClass.key;
      msg.modelKey = targetClass.model?.key || targetClass.modelKey;
    }

    if (msg.type === 'list_created') {
      const listKey = msg.args[0].listKey as string;
      if (listKey.startsWith('__global_list__')) {
        return valueToReturn;
      }
    }

    if (msg.listKey?.startsWith('__global_list__')) {
      return valueToReturn;
    }

    const end = perf.now();

    const duration = end - start;
    const roundedDuration = Math.round(duration * 1000) / 1000;
    msg.duration = roundedDuration;
    msg.start = start;
    msg.end = end;

    const value: AnalyticsEntrySdkActionValue = {
      duration: msg.duration,
    };

    if (msg.modelKey) {
      value.model = msg.modelKey;
    }

    if (msg.connectMessageResult) {
      msg.result = valueToReturn;
    }

    if (msg.args) {
      value.args = msg.args;
    }

    cdRef?.trigger(msg);

    cdRef.analyticsController.mark({
      name: `${msg.type}:end`,
      context: context,
    });

    cdRef.analyticsController.measure({
      name: msg.type,
      start: `${msg.type}:start`,
      end: `${msg.type}:end`,
      context: context,
      detail: value,
    });

    return valueToReturn;
  } catch (err) {
    return result;
  }
};

export const trigger = (_msg: DcuplUpdateMessage): any => {
  const log = (_target: any, _propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor => {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]): any {
      const start = perf.now();

      const cdRef: ChangeDetector = (this as any).cdRef;

      const msg = clone(_msg, true);

      if (!msg.key) {
        cdRef.increaseCounter();
        msg.key = cdRef.getCounter();
      }

      const context = generateKey();

      cdRef.analyticsController.mark({
        name: `${msg.type}:start`,
        context,
      });

      const modelKey = (this as any).model?.key;

      if (!msg.originCdRefKey && cdRef.key) {
        msg.originCdRefKey = cdRef.key;
      }

      if (modelKey) {
        msg.modelKey = modelKey;
      }

      try {
        const result = originalMethod.apply(this, args);

        msg.method = _propertyKey;
        msg.args = args;

        if (result && typeof result === 'object' && 'then' in result) {
          return result.then((result: any) => {
            return finish(this, cdRef, msg, start, context, result);
          });
        } else {
          return finish(this, cdRef, msg, start, context, result);
        }
      } catch (err) {
        throw err;
      }
    };
    return descriptor;
  };

  return log;
};
